import React, { useState, useEffect } from 'react';
import {Modal, Typography, Divider, Spin, App, Space, Card, Button, Input, Timeline, Row, Col} from 'antd';
import { deviceCashTracking } from '../services/posApi';
import NumbersKeypad from "./Sale/NumbersKeypad";
import VirtualKeyboard from "./Sale/Keyboard";

const { Title, Text } = Typography;

const CashTrackingModal = ({ visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [sessionData, setSessionData] = useState(null);
    const [mode, setMode] = useState('info'); // 'info', 'start', 'close', 'add_cash', 'remove_cash'
    const [formData, setFormData] = useState({});
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const { message: messageApi } = App.useApp();
    const [showKeyboard, setShowKeyboard] = useState(false);

    // Форматирование даты
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const months = [
                'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
                'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
            ];

            const day = date.getDate();
            const month = months[date.getMonth()];
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');

            return `Обновлено ${day} ${month} в ${hours}:${minutes}`;
        } catch (error) {
            console.error('Ошибка форматирования даты:', error);
            return 'Дата не определена';
        }
    };

    // Форматирование даты для timeline
    const formatTimelineDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');

            return `${day}.${month}.${year} в ${hours}:${minutes}`;
        } catch (error) {
            console.error('Ошибка форматирования даты:', error);
            return 'Дата не определена';
        }
    };

    // Получение типа активности
    const getActivityTypeName = (type) => {
        const types = {
            'start_tracking': 'Начало сессии трекинга',
            'added_cash': 'Вложение наличных',
            'removed_cash': 'Изъятие наличных',
            'stop_tracking': 'Конец сессии трекинга'
        };
        return types[type] || type;
    };

    // Загрузка информации о сессии
    const loadSessionInfo = async () => {
        setLoading(true);
        try {
            const response = await deviceCashTracking.getSessionInfo();

            if (response.status === 'success') {
                setSessionData(response.data);
                setMode('info');
            } else {
                // Проверяем и status и result для обработки разных форматов ответа
                const isNoActiveSession =
                    (response.status === 'error' && response.code === 'no_active_session') ||
                    (response.result === 'error' && response.code === 'no_active_session');

                if (isNoActiveSession) {
                    setMode('start');
                    await loadFormData();
                } else {
                    messageApi.error('Ошибка при получении данных о сессии');
                    setMode('error');
                }
            }
        } catch (error) {
            console.error('Ошибка загрузки информации о сессии:', error);

            // Проверяем ошибку 400 с кодом no_active_session в response.data
            if (error.response?.status === 400) {
                const errorData = error.response.data;
                if (errorData?.code === 'no_active_session' ||
                    (errorData?.status === 'error' && errorData?.code === 'no_active_session')) {
                    setMode('start');
                    await loadFormData();
                    return;
                }
            }

            messageApi.error('Не удалось загрузить информацию о сессии');
            setMode('error');
        } finally {
            setLoading(false);
        }
    };

    // Загрузка данных формы
    const loadFormData = async () => {
        try {
            const response = await deviceCashTracking.getCashTrackingFormData();
            if (response.status === 'success') {
                setFormData(response.data);
                setAmount(response.data.before_closing_actual?.toString() || '');
            }
        } catch (error) {
            console.error('Ошибка загрузки данных формы:', error);
        }
    };

    // Обработка нажатия клавиш цифровой клавиатуры
    const handleKeyPress = (key) => {
        if (key === 'backspace') {
            setAmount(prev => prev.slice(0, -1));
        } else if (key === '00') {
            setAmount(prev => prev + '00');
        } else {
            setAmount(prev => prev + key);
        }
    };

    // Обработка нажатия виртуальной клавиатуры (только для заметок)
    const handleVirtualKeyPress = (key) => {
        if (key === 'Backspace') {
            setNote((prev) => prev.slice(0, -1));
        } else {
            setNote((prev) => prev + key);
        }
    };

    // Компонент поля заметки с виртуальной клавиатурой
    const renderNoteInput = () => (
        <Input
            placeholder="Заметка"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onFocus={() => setShowKeyboard(true)}
            size="large"
        />
    );

    // Компонент поля суммы (без виртуальной клавиатуры)
    const renderAmountInput = () => (
        <Input
            size="large"
            placeholder="Сумма"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{
                fontSize: '24px',
                textAlign: 'center',
                marginBottom: 16,
                height: '60px',
            }}
            suffix="₸"
        />
    );

    // Начало сессии
    const handleStartSession = async () => {
        if (!amount) {
            messageApi.error('Введите сумму для начала сессии');
            return;
        }

        setActionLoading(true);
        try {
            const response = await deviceCashTracking.startSession(parseFloat(amount));

            if (response.status === 'success') {
                messageApi.success('Сессия успешно открыта');
                setAmount('');
                setNote('');
                await loadSessionInfo();
            } else {
                messageApi.error(response.message || 'Ошибка при открытии сессии');
            }
        } catch (error) {
            console.error('Ошибка при открытии сессии:', error);
            messageApi.error('Не удалось открыть сессию');
        } finally {
            setActionLoading(false);
        }
    };

    // Завершение сессии
    const handleCloseSession = async () => {
        if (!amount) {
            messageApi.error('Введите сумму для закрытия сессии');
            return;
        }

        setActionLoading(true);
        try {
            const response = await deviceCashTracking.closeSession(parseFloat(amount), note);

            if (response.status === 'success') {
                messageApi.success('Сессия успешно закрыта');
                setAmount('');
                setNote('');
                handleModalClose();
            } else {
                messageApi.error(response.message || 'Ошибка при закрытии сессии');
            }
        } catch (error) {
            console.error('Ошибка при закрытии сессии:', error);
            messageApi.error('Не удалось закрыть сессию');
        } finally {
            setActionLoading(false);
        }
    };

    // Создание транзакции
    const handleCreateTransaction = async (type) => {
        if (!amount) {
            messageApi.error('Введите сумму для транзакции');
            return;
        }

        setActionLoading(true);
        try {
            const response = await deviceCashTracking.createTransactions(parseFloat(amount), type, note);

            // Проверяем и status и result для обработки разных форматов ответа
            const isSuccess = response.status === 'success' || response.result === 'success';

            if (isSuccess) {
                messageApi.success('Транзакция успешно создана');
                setAmount('');
                setNote('');
                await loadSessionInfo();
            } else {
                messageApi.error(response.message || 'Ошибка при создании транзакции');
            }
        } catch (error) {
            console.error('Ошибка при создании транзакции:', error);
            messageApi.error('Не удалось создать транзакцию');
        } finally {
            setActionLoading(false);
        }
    };

    // Загрузка данных при открытии модала
    useEffect(() => {
        if (visible) {
            loadSessionInfo();
        }
    }, [visible]);

    // Сброс данных при закрытии модала
    const handleModalClose = () => {
        setSessionData(null);
        setMode('info');
        setAmount('');
        setNote('');
        setShowKeyboard(false);
        onClose();
    };

    // Получение заголовка модала
    const getModalTitle = () => {
        switch (mode) {
            case 'start':
                return 'Начать сессию трекинга';
            case 'close':
                return 'Завершить сессию';
            case 'add_cash':
                return 'Внести наличные';
            case 'remove_cash':
                return 'Изъять наличные';
            default:
                return 'Трекинг наличных';
        }
    };

    // Рендер содержимого информации о сессии
    const renderSessionInfo = () => (
        <div style={{maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden'}}>
            <Space direction="vertical" size="large" style={{width: '100%'}}>
                <div>
                    {/* Дата обновления */}
                    <Text type="secondary" style={{fontSize: '14px'}}>
                        {formatDate(sessionData.date_time)}
                    </Text>
                    {/* Информация о кассе и смене */}
                    <Space direction="vertical" size="small" style={{width: '100%'}}>
                        <Text type="secondary" style={{fontSize: '14px'}}>
                            ID кассы: {sessionData.cash_register_id},
                            ID смены: {sessionData.session_id}
                        </Text>
                    </Space>
                </div>

                <div>
                    {/* Общая сумма */}
                    <Title level={3} style={{margin: 0, fontSize: '24px', fontWeight: 600}}>
                        {parseFloat(sessionData.total_amount).toLocaleString()} ₸
                    </Title>

                    {/* Ожидаемый баланс наличных */}
                    <Text type="secondary" style={{fontSize: '14px'}}>
                        Ожидаемый баланс наличных
                    </Text>
                </div>


                {/* Кнопки изъятия и внесения */}
                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                    <Button
                        type="default"
                        onClick={() => setMode('remove_cash')}
                        style={{
                            marginRight: '8px',
                            flex: 1,
                            height: '70px',
                            fontSize: '18px',
                            lineHeight: '23px',
                            letterSpacing: '-0.44',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >

                        Изъять наличные
                    </Button>
                    <Button
                        type="default"
                        onClick={() => setMode('add_cash')}
                        style={{
                            marginRight: '8px',
                            flex: 1,
                            height: '70px',
                            fontSize: '18px',
                            lineHeight: '23px',
                            letterSpacing: '-0.44',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >
                        Внести наличные
                    </Button>
                </Space>

                {/* Платежи наличными */}
                {sessionData.cash_transactions && (
                    <Card
                        size="small"
                        bordered={false}
                        style={{backgroundColor: 'var(--ant-color-fill-tertiary)'}}
                    >
                        <Space direction="vertical" size="small" style={{width: '100%'}}>
                            <Text type="secondary" style={{fontSize: '16px', fontWeight: 500}}>
                                Платежи наличными
                            </Text>

                            <Divider style={{margin: '8px 0'}}/>

                            <Space direction="vertical" size="small" style={{width: '100%'}}>
                                {/* Валовые платежи */}
                                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Text style={{fontSize: '14px'}}>
                                        Продажи
                                    </Text>
                                    <Text style={{fontSize: '14px', fontWeight: 500}}>
                                        {parseFloat(sessionData.cash_transactions.sales_sum).toLocaleString()} ₸
                                    </Text>
                                </Space>

                                {/* Возвраты */}
                                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Text style={{fontSize: '14px'}}>
                                        Возвраты
                                    </Text>
                                    <Text style={{fontSize: '14px', fontWeight: 500}}>
                                        {parseFloat(sessionData.cash_transactions.refunds_sum).toLocaleString()} ₸
                                    </Text>
                                </Space>

                                {/* Чистые платежи */}
                                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                                    <Text style={{fontSize: '14px'}}>
                                        Чистые платежи
                                    </Text>
                                    <Text style={{fontSize: '14px', fontWeight: 600}}>
                                        {parseFloat(sessionData.cash_transactions.net_sum).toLocaleString()} ₸
                                    </Text>
                                </Space>
                            </Space>
                        </Space>
                    </Card>
                )}

                {/* История активности */}
                {sessionData.cash_activity && sessionData.cash_activity.length > 0 && (
                    <Card
                        size="small"
                        bordered={false}
                        style={{backgroundColor: 'var(--ant-color-fill-tertiary)'}}
                    >
                        <Timeline
                            mode="left"
                            items={sessionData.cash_activity.map((activity) => ({
                                children: (
                                    <Row justify="space-between" align="top">
                                        <Col flex="auto">
                                            <Space direction="vertical" size={0}>
                                                <Text strong style={{fontSize: 14}}>
                                                    {getActivityTypeName(activity.type)}
                                                </Text>
                                                <Text type="secondary" style={{fontSize: 12}}>
                                                    {activity.note}
                                                </Text>
                                                <Text type="secondary" style={{fontSize: 12}}>
                                                    {activity.cashier_name} ({activity.cashier_id}), {formatTimelineDate(activity.created_at)}
                                                </Text>
                                            </Space>
                                        </Col>
                                        <Col>
                                            <Text strong style={{fontSize: 14, whiteSpace: 'nowrap'}}>
                                                {activity.amount ? `${parseFloat(activity.amount).toLocaleString()} ₸` : ''}
                                            </Text>
                                        </Col>
                                    </Row>
                                ),
                            }))}
                        />
                    </Card>
                )}
            </Space>
        </div>
    );

    // Рендер формы начала сессии
    const renderStartForm = () => (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Input
                size="large"
                placeholder="Сумма"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{
                    fontSize: '24px',
                    textAlign: 'center',
                    marginBottom: 16,
                    height: '60px',
                }}
                suffix="₸"
            />

            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <NumbersKeypad onKeyPress={handleKeyPress} />
            </div>

            <Button
                type="primary"
                size="large"
                onClick={handleStartSession}
                loading={actionLoading}
                style={{ width: '100%' }}
            >
                Открыть сессию
            </Button>
        </Space>
    );

    // Рендер формы закрытия сессии
    const renderCloseForm = () => (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
                {renderAmountInput()}
                {renderNoteInput()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <NumbersKeypad onKeyPress={handleKeyPress} />
            </div>

            <Button
                type="primary"
                size="large"
                onClick={handleCloseSession}
                loading={actionLoading}
                style={{
                    height: '70px',
                    fontSize: '18px',
                    fontWeight: '600',
                    width: '100%',
                }}
            >
                Завершить
            </Button>
        </Space>
    );

    // Рендер формы транзакции (внесение/изъятие)
    const renderTransactionForm = () => (
        <Space direction="vertical" size="large" style={{width: '100%'}}>
            <div>
                {renderAmountInput()}
                {renderNoteInput()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <NumbersKeypad onKeyPress={handleKeyPress} />
            </div>

            <Button
                type="primary"
                size="large"
                onClick={() => handleCreateTransaction(mode === 'add_cash' ? 'added_cash' : 'removed_cash')}
                loading={actionLoading}
                style={{
                    height: '70px',
                    fontSize: '18px',
                    lineHeight: '23px',
                    letterSpacing: '-0.44',
                    fontWeight: '600',
                    width: '100%'
                }}
            >
                Готово
            </Button>
        </Space>
    );

    // Рендер содержимого модала
    const renderContent = () => {
        if (loading) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Spin size="large" />
                </Space>
            );
        }

        switch (mode) {
            case 'start':
                return renderStartForm();
            case 'close':
                return renderCloseForm();
            case 'add_cash':
            case 'remove_cash':
                return renderTransactionForm();
            case 'info':
                return sessionData ? renderSessionInfo() : (
                    <Space
                        direction="vertical"
                        size="large"
                        style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                    >
                        <Text type="secondary">Нет данных для отображения</Text>
                    </Space>
                );
            default:
                return (
                    <Space
                        direction="vertical"
                        size="large"
                        style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                    >
                        <Text type="secondary">Произошла ошибка</Text>
                    </Space>
                );
        }
    };

    return (
        <>
            <Modal
                title={getModalTitle()}
                open={visible}
                onCancel={handleModalClose}
                footer={mode === 'info' && sessionData ? (
                    <Button
                        type="primary"
                        onClick={() => setMode('close')}
                        size="large"
                        style={{
                            height: '70px',
                            fontSize: '18px',
                            fontWeight: '600',
                            width: '100%',
                        }}
                    >
                        Завершить сессию
                    </Button>
                ) : null}
                width={450}
                centered
                style={{ height: '600px' }}
                bodyStyle={{
                    height: mode === 'info' && sessionData ? '500px' : 'auto',
                    overflowY: mode === 'info' && sessionData ? 'auto' : 'visible',
                    overflowX: 'hidden',
                }}
            >
                {renderContent()}
            </Modal>

            {/* Виртуальная клавиатура только для заметок */}
            <VirtualKeyboard
                visible={showKeyboard}
                onKeyPress={handleVirtualKeyPress}
                onClose={() => setShowKeyboard(false)}
            />
        </>
    );
};

export default CashTrackingModal;